import React from 'react';
import { Link } from 'react-router-dom';
import type { ApprovalOrigin } from '../client';
import { useChatDock } from '../state/ChatDockContext';

export function relativeTime(value: string): string {
  const milliseconds = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(milliseconds / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export interface ApprovalCardHeaderAgent {
  id?: number | null;
  name: string;
}

export interface ApprovalCardHeaderRequestingUser {
  id: number;
  name: string;
}

const ASKING_TRIGGERS = new Set(['direct', 'chat', 'card']);

const UNATTENDED_TRIGGER_LABELS: Record<string, string> = {
  loop_task: 'a loop task',
  scheduled_task: 'a scheduled task',
  card_monitor: 'a card monitor',
  column_monitor: 'a column monitor',
  workspace_monitor: 'a workspace monitor',
  deferred_wait: 'a deferred wait',
  card_next_action: 'a card next action',
  card_watch: 'a card watch',
  inbound_email: 'an inbound email',
  inbound_channel: 'an inbound channel message',
};

/**
 * "On behalf of whom" — the platform's record of the human behind the turn,
 * never the agent's prose. A human who asked is named; an unattended trigger
 * says so; a record that predates the field says nothing rather than guessing.
 */
function requesterSuffix(
  requestingUser: ApprovalCardHeaderRequestingUser | null | undefined,
  requestingTrigger: string | null | undefined,
): React.ReactNode {
  if (requestingUser) {
    return (
      <span className="approval-on-behalf">
        {' on behalf of '}
        <strong>{requestingUser.name}</strong>
      </span>
    );
  }
  if (!requestingTrigger) return null;
  if (ASKING_TRIGGERS.has(requestingTrigger)) {
    return (
      <span className="approval-on-behalf">
        {' on behalf of a former team member'}
      </span>
    );
  }
  const label =
    UNATTENDED_TRIGGER_LABELS[requestingTrigger] ||
    requestingTrigger.replace(/_/g, ' ');
  return (
    <span className="approval-on-behalf approval-unattended">
      {` — unattended, raised by ${label}; no one asked`}
    </span>
  );
}

/**
 * Shared header for every approval card: a clickable agent-name link
 * ("who is asking") plus a named "from {origin}" affordance ("what this is
 * about" — a project card, a team chat, or an external conversation) and a
 * relative-time stamp.
 */
export const ApprovalCardHeader: React.FC<{
  teamId: number | string;
  agent: ApprovalCardHeaderAgent;
  origin: ApprovalOrigin | null | undefined;
  requestingUser?: ApprovalCardHeaderRequestingUser | null;
  requestingTrigger?: string | null;
  createdAt?: string;
  title?: string;
}> = ({
  teamId,
  agent,
  origin,
  requestingUser,
  requestingTrigger,
  createdAt,
  title,
}) => {
  const { openConversation } = useChatDock();

  const cardPath =
    origin?.kind === 'project_card' && origin.projectId && origin.cardId
      ? `/console/team/${origin.teamId}/projects/${origin.projectId}/cards/${origin.cardId}`
      : null;
  const externalConversationPath =
    origin?.kind === 'external_conversation' &&
    origin.conversationId &&
    agent.id
      ? `/console/team/${origin.teamId}/agent/${agent.id}/conversations/${origin.conversationId}`
      : null;

  const namedLabel = (fallback: string) =>
    origin?.name ? `from ${origin.name}` : fallback;

  let originNode: React.ReactNode = null;
  if (cardPath) {
    originNode = (
      <Link className="approval-origin-link" to={cardPath}>
        {namedLabel('Open card for context')}
      </Link>
    );
  } else if (externalConversationPath) {
    originNode = (
      <Link className="approval-origin-link" to={externalConversationPath}>
        {namedLabel('Open conversation for context')}
      </Link>
    );
  } else if (origin?.kind === 'chat_conversation' && origin.conversationId) {
    const conversationId = origin.conversationId;
    originNode = (
      <button
        type="button"
        className="approval-link-button"
        onClick={() => openConversation(String(origin.teamId), conversationId)}>
        {namedLabel('Open conversation for context')}
      </button>
    );
  }

  return (
    <>
      <div className="approval-card-head">
        <div>
          <div className="approval-requester" data-testid="approval-requester">
            {agent.id != null ? (
              <Link
                className="approval-agent"
                to={`/console/team/${teamId}/agent/${agent.id}`}>
                {agent.name}
              </Link>
            ) : (
              <span className="approval-agent">{agent.name}</span>
            )}
            {requesterSuffix(requestingUser, requestingTrigger)}
          </div>
          {title ? <h3>{title}</h3> : null}
        </div>
        {createdAt ? (
          <time dateTime={createdAt}>{relativeTime(createdAt)}</time>
        ) : null}
      </div>
      <div className="approval-context-link">{originNode}</div>
    </>
  );
};
